import { useCallback, useEffect, useState } from 'react';
import { api, type Veranstaltung } from '../api/client';
import { Feld, Karte, Meldung, StatusPill, fehlerText } from '../components/ui';
import { ART_TEXT, ausLokal, datumZeit } from '../format';
import { gehe, link } from '../router';

const ANORDNUNG_TEXT = {
  fehlt: 'nicht erfasst',
  entwurf: 'erfasst, Freigabe offen',
  korrektur: 'in Korrektur',
  freigegeben: 'freigegeben',
} as const;

const SCHRITT_TEXT = {
  vorgaben: ['vorgaben', 'Vorgaben übernehmen'],
  route: ['planung', 'Route & Abgleich'],
  kraefte: ['kraefte', 'Kräfte zuordnen'],
  ausgeben: ['ausgabe', 'Ausgeben'],
} as const;

/** Skizze 1: Tabelle der Veranstaltungen, rechts „Nächster Einsatz“ mit offenen Punkten. */
export function Uebersicht({ darf }: { darf: boolean }) {
  const [liste, setListe] = useState<Veranstaltung[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kopie, setKopie] = useState<Veranstaltung | null>(null);

  const laden = useCallback(() => {
    api
      .veranstaltungen()
      .then((r) => setListe(r.veranstaltungen))
      .catch((e: unknown) => setFehler(fehlerText(e)));
  }, []);
  useEffect(laden, [laden]);

  const jetzt = Date.now();
  const anstehend = (liste ?? []).filter((v) => Date.parse(v.startzeit) >= jetzt - 6 * 3600_000);
  const naechste = [...anstehend].sort(
    (a, b) => Date.parse(a.startzeit) - Date.parse(b.startzeit),
  )[0];

  async function loeschen(v: Veranstaltung) {
    if (!window.confirm(`„${v.titel}“ mit allen Planungsdaten endgültig löschen?`)) return;
    try {
      await api.veranstaltungLoeschen(v.id);
      laden();
    } catch (e) {
      setFehler(fehlerText(e));
    }
  }

  return (
    <div className="seite seite--zweispaltig">
      <div>
        <Karte
          titel="Veranstaltungen"
          aktion={
            darf && (
              <a className="knopf knopf--primaer" href={link('neu')}>
                Neue Veranstaltung
              </a>
            )
          }
        >
          {fehler && <Meldung art="fehler">{fehler}</Meldung>}
          {liste === null ? (
            <p>Lädt …</p>
          ) : liste.length === 0 ? (
            <p className="gedaempft">Noch keine Veranstaltung angelegt.</p>
          ) : (
            <table className="tabelle">
              <thead>
                <tr>
                  <th>Termin</th>
                  <th>Veranstaltung</th>
                  <th>Anordnung</th>
                  <th>Planungsschritt</th>
                  <th>Hinweise</th>
                  {darf && <th aria-label="Aktionen" />}
                </tr>
              </thead>
              <tbody>
                {liste.map((v) => {
                  const schritt = SCHRITT_TEXT[v.schritt ?? 'vorgaben'];
                  return (
                    <tr key={v.id}>
                      <td>{datumZeit(v.startzeit)}</td>
                      <td>
                        <a href={link('v', v.id, schritt[0])}>{v.titel}</a>
                        <div className="gedaempft">
                          {ART_TEXT[v.art] ?? v.art} · {v.teilnehmer} Teilnehmende
                        </div>
                      </td>
                      <td>
                        <StatusPill
                          status={
                            v.anordnung_status === 'freigegeben'
                              ? 'gruen'
                              : v.anordnung_status === 'fehlt'
                                ? 'rot'
                                : 'gelb'
                          }
                          text={ANORDNUNG_TEXT[v.anordnung_status ?? 'fehlt']}
                        />
                      </td>
                      <td>
                        <a href={link('v', v.id, schritt[0])}>{schritt[1]}</a>
                      </td>
                      <td>
                        <Hinweise v={v} />
                      </td>
                      {darf && (
                        <td className="tabelle__aktionen">
                          <button type="button" className="knopf" onClick={() => setKopie(v)}>
                            Als Vorlage kopieren
                          </button>
                          <button
                            type="button"
                            className="knopf knopf--gefahr"
                            onClick={() => void loeschen(v)}
                          >
                            Löschen
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Karte>
        {kopie && <KopieDialog quelle={kopie} onFertig={() => setKopie(null)} />}
      </div>

      <aside>
        <Karte titel="Nächster Einsatz">
          {!naechste ? (
            <p className="gedaempft">Kein anstehender Einsatz.</p>
          ) : (
            <>
              <h3>{naechste.titel}</h3>
              <p>{datumZeit(naechste.startzeit)}</p>
              <ul className="offene-punkte">
                {naechste.anordnung_status !== 'freigegeben' && (
                  <li>
                    <a href={link('v', naechste.id, 'vorgaben')}>
                      Vorgabe: {ANORDNUNG_TEXT[naechste.anordnung_status ?? 'fehlt']}
                    </a>
                  </li>
                )}
                {!naechste.analyse && (
                  <li>
                    <a href={link('v', naechste.id, 'planung')}>Route zeichnen und prüfen</a>
                  </li>
                )}
                {naechste.analyse && naechste.analyse.rot > 0 && (
                  <li>
                    <a href={link('v', naechste.id, 'planung')}>
                      {naechste.analyse.rot} rote Befunde klären
                    </a>
                  </li>
                )}
                {naechste.analyse && naechste.analyse.gelb_offen > 0 && (
                  <li>
                    <a href={link('v', naechste.id, 'kraefte')}>
                      {naechste.analyse.gelb_offen} gelbe Punkte offen
                    </a>
                  </li>
                )}
                {naechste.analyse?.vollstaendig && <li>Plan vollständig – Unterlagen ausgeben.</li>}
              </ul>
              <a
                className="knopf knopf--primaer"
                href={link('v', naechste.id, SCHRITT_TEXT[naechste.schritt ?? 'vorgaben'][0])}
              >
                Weiter planen
              </a>
            </>
          )}
        </Karte>
      </aside>
    </div>
  );
}

function Hinweise({ v }: { v: Veranstaltung }) {
  if (!v.analyse) return <span className="gedaempft">noch nicht geprüft</span>;
  if (v.analyse.vollstaendig) return <StatusPill status="gruen" text="vollständig" />;
  return (
    <span className="hinweise">
      {v.analyse.rot > 0 && <StatusPill status="rot" text={`${v.analyse.rot} rot`} />}
      {v.analyse.gelb_offen > 0 && (
        <StatusPill status="gelb" text={`${v.analyse.gelb_offen} gelb`} />
      )}
    </span>
  );
}

function KopieDialog({ quelle, onFertig }: { quelle: Veranstaltung; onFertig: () => void }) {
  const jahr = new Date(quelle.startzeit).getFullYear() + 1;
  const [titel, setTitel] = useState(quelle.titel.replace(/\d{4}/, String(jahr)));
  const [start, setStart] = useState(quelle.startzeit.slice(0, 16).replace(/^\d{4}/, String(jahr)));
  const [fehler, setFehler] = useState<string | null>(null);

  async function kopieren() {
    try {
      const r = await api.veranstaltungKopieren(quelle.id, titel, ausLokal(start) ?? '');
      gehe(link('v', r.veranstaltung.id, 'eckdaten'));
    } catch (e) {
      setFehler(fehlerText(e));
    }
  }

  return (
    <Karte titel={`Vorlage aus „${quelle.titel}“`}>
      <p className="gedaempft">
        Übernommen werden Route, Einsatzabschnitte, Posten und die Anordnung als neuer Entwurf (ohne
        PDF, Zeiten auf das neue Datum verschoben). Die neue Anordnung muss geprüft und erneut
        freigegeben werden.
      </p>
      <Feld label="Titel">
        <input value={titel} onChange={(e) => setTitel(e.target.value)} />
      </Feld>
      <Feld label="Start">
        <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
      </Feld>
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
      <div className="knopfreihe">
        <button type="button" className="knopf knopf--primaer" onClick={() => void kopieren()}>
          Kopie anlegen
        </button>
        <button type="button" className="knopf" onClick={onFertig}>
          Abbrechen
        </button>
      </div>
    </Karte>
  );
}
